import { z } from 'zod';
import { RegimeTributario, SituacaoCliente, UF } from './enums.js';
import { CompetenciaChave, DataISO, Id, Paginacao, limparDocumento, soDigitos } from './comum.js';
import { cnpjValido, cpfValido } from './documento.js';

export const TipoPessoa = z.enum(['PJ', 'PF']);
export type TipoPessoa = z.infer<typeof TipoPessoa>;

const AnexoSimples = z.number().int().min(1).max(5);
const ROMANOS = ['', 'I', 'II', 'III', 'IV', 'V'];
/** Os anexos de uma faixa de regime: a lista nova; em registro antigo (lista vazia), o anexo único. Sem repetir, em ordem. */
export function anexosDoRegime(r: { anexoSimples?: number | null; anexosSimples?: number[] | null }): number[] {
  const lista = r.anexosSimples?.length ? r.anexosSimples : r.anexoSimples != null ? [r.anexoSimples] : [];
  return [...new Set(lista)].filter((n) => n >= 1 && n <= 5).sort((a, b) => a - b);
}
/** "I", "I e III", "I, III e V" */
export function rotuloAnexos(anexos: number[]): string {
  const t = anexos.map((n) => ROMANOS[n] ?? String(n));
  return t.length <= 1 ? t[0] ?? '' : `${t.slice(0, -1).join(', ')} e ${t[t.length - 1]}`;
}

export const RegimeHistorico = z.object({
  id: Id,
  regime: RegimeTributario,
  /** o principal (o primeiro de `anexosSimples`) — fica para quem ainda lê um anexo só */
  anexoSimples: z.number().int().min(1).max(5).nullable(),
  /** todos os anexos em que a empresa fatura */
  anexosSimples: z.array(AnexoSimples),
  competenciaInicio: CompetenciaChave,
  competenciaFim: CompetenciaChave.nullable(),
  observacao: z.string().nullable(),
});
export type RegimeHistorico = z.infer<typeof RegimeHistorico>;

export const RegimeHistoricoBody = z.object({
  regime: RegimeTributario,
  anexoSimples: z.number().int().min(1).max(5).nullable().optional(),
  /** mais de um anexo (comércio + serviço): manda a lista; `anexoSimples` sozinho continua aceito */
  anexosSimples: z.array(AnexoSimples).max(5).optional(),
  competenciaInicio: CompetenciaChave,
  competenciaFim: CompetenciaChave.nullable().optional(),
  observacao: z.string().trim().max(200).nullable().optional(),
}).refine((r) => r.competenciaFim == null || r.competenciaFim >= r.competenciaInicio, {
  message: 'competência final não pode ser anterior à inicial', path: ['competenciaFim'],
}).refine((r) => r.regime !== 'SIMPLES' || anexosDoRegime(r).length > 0, {
  message: 'informe o anexo do Simples (um ou mais)', path: ['anexosSimples'],
});
export type RegimeHistoricoBody = z.infer<typeof RegimeHistoricoBody>;

export const Responsavel = z.object({ departamentoId: Id, membroId: Id });
export type Responsavel = z.infer<typeof Responsavel>;

const documentoSchema = z.string().transform(limparDocumento);

export const CnaeItem = z.object({ codigo: z.string().trim().min(1).max(12), descricao: z.string().trim().max(250).default('') });
export type CnaeItem = z.infer<typeof CnaeItem>;
/** Sócio ou administrador do quadro societário. O documento vem mascarado da Receita (***123456**) — guarda-se como veio. */
export const SocioItem = z.object({ nome: z.string().trim().min(2).max(160), documento: z.string().trim().max(20).nullable().default(null), qualificacao: z.string().trim().max(120).nullable().default(null), desde: DataISO.nullable().default(null) });
export type SocioItem = z.infer<typeof SocioItem>;

export const ClienteBase = z.object({
  codigo: z.string().trim().max(20).nullable().optional(),
  tipoPessoa: TipoPessoa.default('PJ'),
  documento: documentoSchema,
  razaoSocial: z.string().trim().min(2).max(200),
  nomeFantasia: z.string().trim().max(120).nullable().optional(),
  inscricaoEstadual: z.string().trim().max(20).nullable().optional(),
  inscricaoMunicipal: z.string().trim().max(20).nullable().optional(),
  uf: UF,
  municipioIbge: z.number().int().nullable().optional(),
  municipioNome: z.string().trim().max(80).nullable().optional(),
  situacao: SituacaoCliente.default('ATIVA'),
  inicioContrato: DataISO.nullable().optional(),
  dataEncerramento: DataISO.nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  telefone: z.string().trim().max(30).nullable().optional(),
  observacoes: z.string().trim().max(4000).nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(30)).max(20).default([]),
  /** PF: necessária para a Consulta CPF */
  dataNascimento: DataISO.nullable().optional(),
  /** o que contrato e ficha cadastral pedem */
  endereco: z.string().trim().max(300).nullable().optional(),
  representanteNome: z.string().trim().max(120).nullable().optional(),
  /** com ou sem pontuação */
  representanteCpf: z.string().trim().max(14).nullable().optional(),
  representanteCargo: z.string().trim().max(60).nullable().optional(),
  /** endereço separado — o `endereco` em linha é montado destes pelo servidor */
  endLogradouro: z.string().trim().max(120).nullable().optional(), endNumero: z.string().trim().max(15).nullable().optional(), endComplemento: z.string().trim().max(80).nullable().optional(),
  endBairro: z.string().trim().max(80).nullable().optional(), /** com ou sem traço */ endCep: z.string().trim().max(9).nullable().optional(),
  /** o que mais vem num cartão CNPJ */
  naturezaJuridica: z.string().trim().max(160).nullable().optional(), dataAbertura: DataISO.nullable().optional(), porte: z.string().trim().max(60).nullable().optional(),
  capitalSocial: z.number().min(0).max(99_999_999_999).nullable().optional(), tipoEstabelecimento: z.string().trim().max(10).nullable().optional(),
  cnaePrincipal: z.string().trim().max(12).nullable().optional(), cnaePrincipalDescricao: z.string().trim().max(250).nullable().optional(),
  cnaesSecundarios: z.array(CnaeItem).max(200).optional(), socios: z.array(SocioItem).max(100).optional(),
  optanteSimples: z.boolean().nullable().optional(), optanteMei: z.boolean().nullable().optional(), dataOpcaoSimples: DataISO.nullable().optional(), dataSituacaoCadastral: DataISO.nullable().optional(),
  telefone2: z.string().trim().max(30).nullable().optional(), nire: z.string().trim().max(20).nullable().optional(),
  /** outros telefones (financeiro, sócio…), com o nome de quem atende */
  telefones: z.array(z.object({ nome: z.string().trim().max(60).nullable().default(null), numero: z.string().trim().min(8).max(30) })).max(20).optional(),
  /** e-mails que recebem documentos, lembretes e avisos sem serem usuários do portal (viram contatos "só avisos") */
  emailsAviso: z.array(z.string().trim().toLowerCase().email().max(200)).max(20).optional(),
  /** só no cadastro: cria as pastas do Drive e as solicitações dos documentos iniciais (contrato social, cartão CNPJ, certificado) */
  onboarding: z.boolean().optional(),
  /** para a ficha que o cliente manda a fornecedores: e-mail que recebe o XML das notas e a conta bancária da empresa */
  emailNfe: z.string().trim().max(200).nullable().optional(), bancoNome: z.string().trim().max(80).nullable().optional(), bancoAgencia: z.string().trim().max(15).nullable().optional(),
  bancoConta: z.string().trim().max(25).nullable().optional(), pixChave: z.string().trim().max(120).nullable().optional(),
});

const validaDocumento = (c: { tipoPessoa: TipoPessoa; documento: string }) =>
  c.tipoPessoa === 'PJ' ? cnpjValido(c.documento) : cpfValido(c.documento);

export const ClienteBody = ClienteBase.extend({
  /** regime vigente na criação (gera a 1ª linha do histórico); opcional na edição */
  regimeInicial: RegimeHistoricoBody.optional(),
}).refine(validaDocumento, { message: 'CNPJ/CPF inválido', path: ['documento'] })
  .refine((c) => c.situacao !== 'BAIXADA' || !!c.dataEncerramento, { message: 'informe a data de encerramento', path: ['dataEncerramento'] });
export type ClienteBody = z.infer<typeof ClienteBody>;

export const Cliente = ClienteBase.extend({
  id: Id,
  documento: z.string(), // sem transform: schema de RESPOSTA (o serializador não aceita transform)
  tipoPessoa: TipoPessoa,
  situacao: SituacaoCliente,
  tags: z.array(z.string()),
  /** última situação cadastral na RFB ("2 Ativa", "8 Baixada"…) e quando foi consultada */
  rfbSituacao: z.string().nullable().optional(),
  rfbConsultadoEm: z.string().nullable().optional(),
  /** na lista: quantas obrigações estão marcadas e quantos contatos ativos (para mostrar o que falta) */
  obrigacoesMarcadas: z.number().int().optional(), contatosAtivos: z.number().int().optional(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
  /** regime vigente na competência atual (derivado do histórico) */
  regimeAtual: RegimeTributario.nullable(),
  anexoAtual: z.number().int().nullable(),
  anexosAtuais: z.array(z.number().int()).default([]),
});
export type Cliente = z.infer<typeof Cliente>;

export const ClienteDetalhe = Cliente.extend({
  regimes: z.array(RegimeHistorico),
  responsaveis: z.array(Responsavel),
});
export type ClienteDetalhe = z.infer<typeof ClienteDetalhe>;

export const ClientesQuery = Paginacao.extend({
  busca: z.string().trim().max(120).optional(),
  situacao: SituacaoCliente.optional(),
  regime: RegimeTributario.optional(),
  uf: UF.optional(),
  tag: z.string().optional(),
  ordem: z.enum(['razaoSocial', 'codigo', 'criadoEm']).default('razaoSocial'),
});
export type ClientesQuery = z.infer<typeof ClientesQuery>;

export const ClientesPagina = z.object({
  itens: z.array(Cliente),
  total: z.number().int(),
  page: z.number().int(),
  perPage: z.number().int(),
});
export type ClientesPagina = z.infer<typeof ClientesPagina>;

// ---- importação em lote (CSV já parseado na tela) ----
export const ImportarClientesBody = z.object({
  /** cada registro é a linha crua do CSV (chave = cabeçalho normalizado) */
  registros: z.array(z.record(z.string(), z.string())).min(1).max(2000),
  confirmar: z.boolean().default(false),
  /** competência inicial do regime importado (AAAAMM); padrão = atual */
  competenciaInicio: CompetenciaChave.optional(),
});
export type ImportarClientesBody = z.infer<typeof ImportarClientesBody>;

export const ImportarClientesResultado = z.object({
  total: z.number().int(),
  validos: z.number().int(),
  invalidos: z.number().int(),
  duplicados: z.number().int(),
  inseridos: z.number().int(),
  /** contatos (quem recebe os e-mails) criados a partir da coluna de e-mail */
  contatosCriados: z.number().int().optional(),
  linhas: z.array(z.object({
    linha: z.number().int(),
    documento: z.string(),
    razaoSocial: z.string(),
    situacao: z.enum(['OK', 'ERRO', 'DUPLICADO']),
    erros: z.array(z.string()),
    /** e-mails válidos da linha: viram contatos do cliente */
    contatos: z.number().int().optional(),
    avisos: z.array(z.string()).optional(),
  })),
});
export type ImportarClientesResultado = z.infer<typeof ImportarClientesResultado>;

/** Cabeçalhos aceitos na importação (normalizados: minúsculo, sem acento/espaço). */
export const COLUNAS_IMPORTACAO: Record<string, string[]> = {
  codigo: ['codigo', 'cod', 'codigointerno'],
  documento: ['documento', 'cnpj', 'cpf', 'cnpjcpf', 'cpfcnpj'],
  razaoSocial: ['razaosocial', 'razao', 'nome', 'empresa'],
  nomeFantasia: ['nomefantasia', 'fantasia', 'apelido'],
  uf: ['uf', 'estado'],
  municipioNome: ['municipio', 'cidade'],
  regime: ['regime', 'regimetributario', 'tributacao'],
  anexoSimples: ['anexo', 'anexosimples'],
  inscricaoEstadual: ['ie', 'inscricaoestadual'],
  inscricaoMunicipal: ['im', 'inscricaomunicipal', 'ccm'],
  endLogradouro: ['logradouro', 'endereco', 'rua'],
  endNumero: ['numero', 'num', 'nro'],
  endComplemento: ['complemento'],
  endBairro: ['bairro'],
  endCep: ['cep'],
  email: ['email', 'emails', 'emailcontato', 'emaildocontato'],
  contatoNome: ['contato', 'nomecontato', 'nomedocontato', 'responsavel', 'socio'],
  telefone: ['telefone', 'fone', 'celular', 'whatsapp'],
  situacao: ['situacao', 'status'],
  inicioContrato: ['iniciocontrato', 'inicio', 'desde'],
  tags: ['tags', 'grupo', 'grupos'],
};

/** Clientes que têm e-mail no cadastro e nenhum contato: vira contato (é para os contatos que os e-mails ao cliente vão). */
export const ContatosDoCadastroResultado = z.object({ clientes: z.number().int(), criados: z.number().int(), semEmail: z.number().int() });

/** "Completar cadastros pela Receita": cada cliente PJ ganha, nos campos em branco, o que a última consulta CNPJ dele já trouxe. */
export const CompletarPelaRfbResultado = z.object({
  clientes: z.number().int(), completados: z.number().int(), semConsulta: z.number().int(),
  itens: z.array(z.object({ clienteId: Id, razaoSocial: z.string(), situacao: z.enum(['COMPLETADO', 'JA_COMPLETO', 'SEM_CONSULTA']), campos: z.array(z.string()) })),
});
export type CompletarPelaRfbResultado = z.infer<typeof CompletarPelaRfbResultado>;
